/**
 * The leading verb clause of a learning outcome.
 *
 * An outcome reads Action Verb + Object + Context, so only the verbs that open
 * it are the learner's actions. Verbs later on describe the object ("a system
 * that enables staff to compare prices") and are not the learner's.
 *
 * @module lib/lo-verb-clause
 */

import { normaliseText } from "./language-detect";

/** A bullet or list number an outcome may be pasted with. */
const LIST_MARKER = /^(?:[-*•]|\d+[.)])\s+/;

/** "On completion of this module, learners will be able to" and its variants. */
const STEM = new RegExp(
  [
    "^(?:(?:on|upon)\\s+(?:the\\s+)?(?:successful\\s+)?completion(?:\\s+of\\s+(?:this|the)\\s+\\w+)?\\s*,?\\s*",
    "|by\\s+the\\s+end\\s+of\\s+(?:this|the)\\s+\\w+\\s*,?\\s*)?",
    "(?:(?:the\\s+)?(?:learners?|students?|graduates?|participants?)\\s+(?:will|should|must|can|shall)\\s+)?",
    "(?:be\\s+able\\s+to\\b)?\\s*:?\\s*",
  ].join(""),
  "i",
);

/** An adverb before a verb, as in "critically evaluate". */
const ADVERB = /^[a-z]+ly\s+/i;

/** What joins verbs in one clause: a comma, "and", "or", or a comma and either. */
const JOINER = /^(?:\s*,\s*(?:(?:and\/or|and|or)\s+)?|\s+(?:and\/or|and|or|&)\s+)/i;

/**
 * The run of known verbs that opens an outcome, after any stem.
 *
 * @param text - The outcome as written.
 * @param verbAtStart - Matches a known verb at the start of a string, word-bounded and not global.
 * @returns The clause as written, e.g. "Analyse and evaluate"; empty when the outcome
 *   does not open with a known verb.
 */
export function leadingVerbClause(text: string, verbAtStart: RegExp): string {
  const rest = normaliseText(text).replace(LIST_MARKER, "").replace(STEM, "");
  let pos = 0;
  let clauseEnd = 0;
  for (;;) {
    let at = pos;
    const adverb = ADVERB.exec(rest.slice(at));
    if (adverb && !verbAtStart.test(rest.slice(at))) {
      at += adverb[0].length;
    }
    const verb = verbAtStart.exec(rest.slice(at));
    if (!verb) {
      break;
    }
    at += verb[0].length;
    clauseEnd = at;
    const joiner = JOINER.exec(rest.slice(at));
    if (!joiner) {
      break;
    }
    pos = at + joiner[0].length;
  }
  return rest.slice(0, clauseEnd);
}
